import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Slide } from '../types';
import SlidesTab from '../components/SlidesTab';
import ShopAdminTab from '../components/ShopAdminTab';
import PlatformAdminTab from '../components/PlatformAdminTab';
import QuickAnalyzeTab from '../components/QuickAnalyzeTab';
import MarketplaceEvalTab from '../components/MarketplaceEvalTab';
import HomeTab from '../components/HomeTab';
import CreateSlideModal from '../components/CreateSlideModal';
type TabType = 'home' | 'marketplaceEval' | 'quickAnalyze' | 'slides' | 'shopAdmin' | 'admin';

const Dashboard: React.FC = () => {
  useAuth(); // Ensure user is authenticated
  const { currentShop, currentMembership, isInternalMember, isPlatformAdmin, userRole } = useShop();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user has appropriate role for Quick Analyze and Marketplace Eval
  // NOTE: All tiers now have access to all features - usage is controlled by rate limits
  const canUseAnalysisFeatures = (() => {
    if (!currentShop || !currentMembership) return false;

    // User must have appropriate role (PLATFORM_ADMIN, OWNER, ADMIN, or MEMBER)
    const allowedRoles = ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER'];
    return currentMembership.roles.some(role => allowedRoles.includes(role));
  })();

  // Get initial tab from URL hash or use default
  // Handles both simple hashes like #shopAdmin and sub-paths like #shopAdmin/subscription
  const getInitialTab = (): TabType => {
    const hash = location.hash.replace('#', '');
    const mainTab = hash.split('/')[0]; // Extract main tab from paths like "shopAdmin/subscription"
    const validTabs: TabType[] = ['home', 'marketplaceEval', 'quickAnalyze', 'slides', 'shopAdmin', 'admin'];
    if (validTabs.includes(mainTab as TabType)) {
      return mainTab as TabType;
    }
    // Default to home tab
    return 'home';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingNewSlide, setPendingNewSlide] = useState<{ slide: Slide; autoAnalyze?: boolean } | null>(null);

  // Check if user can create slides (PLATFORM_ADMIN, OWNER, ADMIN, MEMBER)
  const canManageSlides = (() => {
    if (!currentMembership) return false;
    const allowedRoles = ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER'];
    return currentMembership.roles.some(role => allowedRoles.includes(role));
  })();

  // Update tab when URL hash changes
  // Handles both simple hashes like #shopAdmin and sub-paths like #shopAdmin/subscription
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const mainTab = hash.split('/')[0]; // Extract main tab from paths like "shopAdmin/subscription"
    const validTabs: TabType[] = ['home', 'marketplaceEval', 'quickAnalyze', 'slides', 'shopAdmin', 'admin'];
    if (validTabs.includes(mainTab as TabType)) {
      setActiveTab(mainTab as TabType);
    }
  }, [location.hash]);

  // Reset to slides tab when switching to a shop where current tab is not accessible
  useEffect(() => {
    if (!currentShop || !currentMembership) return;

    // Check if current tab is still valid for this shop/membership
    const internalRoles = ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER', 'VIEWER'];
    const hasInternalAccess = currentMembership.roles.some(role => internalRoles.includes(role));
    const hasPlatformAdmin = currentMembership.roles.includes('PLATFORM_ADMIN');

    // If on My Shop tab but not an internal member, switch to home.
    // (Internal members — incl. MEMBER/VIEWER — get My Shop for Collections.)
    if (activeTab === 'shopAdmin' && !hasInternalAccess) {
      setActiveTab('home');
      navigate('/#home', { replace: true });
    }

    // If on admin tab but not platform admin, switch to home
    if (activeTab === 'admin' && !hasPlatformAdmin) {
      setActiveTab('home');
      navigate('/#home', { replace: true });
    }
  }, [currentShop?.id, currentMembership]);

  // Handle tab change - update both state and URL
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/#${tab}`, { replace: true });
  };

  if (!currentShop) {
    return (
      <div className="dashboard-content">
        <div
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
            borderRadius: '8px',
            padding: '24px',
            marginTop: '40px'
          }}
        >
          <div
            style={{
              borderBottom: '1px solid var(--border-light)',
              paddingBottom: '16px',
              marginBottom: '16px'
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>No Shop Selected</h2>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Please select a shop from the header to continue.</p>
        </div>
      </div>
    );
  }

  const availableTabs = [
    { id: 'home' as TabType, label: 'Home' },
    ...(canManageSlides ? [{ id: 'createSlide' as const, label: 'Create Slide' }] : []),
    ...(canUseAnalysisFeatures ? [{ id: 'marketplaceEval' as TabType, label: 'Marketplace Eval' }] : []),
    { id: 'slides' as TabType, label: 'Slides' },
    ...(isInternalMember ? [{ id: 'shopAdmin' as TabType, label: 'My Shop' }] : []),
    ...(isPlatformAdmin ? [{ id: 'admin' as TabType, label: 'Platform Admin' }] : [])
  ];

  return (
    <>
      {/* Hide scrollbar for webkit browsers */}
      <style>{`
        nav::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-primary)',
          paddingTop: '20px'
        }}
        data-user-role={userRole || undefined}
      >
      <div className="dashboard-content">
        {/* Shop Header */}
        <div
          style={{
            marginBottom: '32px'
          }}
        >
          <div>
            <div>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {currentShop.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '32px' }}>
          <nav
            style={{
              display: 'flex',
              gap: '4px',
              backgroundColor: 'var(--bg-secondary)',
              padding: '4px',
              borderRadius: '8px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'createSlide') {
                    setShowCreateModal(true);
                  } else {
                    handleTabChange(tab.id as TabType);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--text-inverse)' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'home' && (
            <HomeTab />
          )}
          {activeTab === 'marketplaceEval' && canUseAnalysisFeatures && (
            <MarketplaceEvalTab
              shopId={currentShop.id}
              onSaveAndEdit={(slideId: string) => {
                // Switch to slides tab and trigger edit
                setActiveTab('slides');
                navigate(`/#slides?edit=${slideId}`, { replace: true });
              }}
            />
          )}
          {activeTab === 'quickAnalyze' && canUseAnalysisFeatures && (
            <QuickAnalyzeTab
              shopId={currentShop.id}
              onSaveAndEdit={(slideId: string) => {
                // Switch to slides tab and trigger edit
                setActiveTab('slides');
                navigate(`/#slides?edit=${slideId}`, { replace: true });
              }}
            />
          )}
          {activeTab === 'slides' && (
            <SlidesTab
              shopId={currentShop.id}
              pendingNewSlide={pendingNewSlide}
              onPendingNewSlideConsumed={() => setPendingNewSlide(null)}
            />
          )}
          {activeTab === 'shopAdmin' && isInternalMember && (
            <ShopAdminTab shopId={currentShop.id} />
          )}
          {activeTab === 'admin' && isPlatformAdmin && (
            <PlatformAdminTab />
          )}
        </div>
      </div>
      </div>

      {/* Create Slide Modal - rendered at Dashboard level for instant open */}
      {showCreateModal && currentShop && (
        <CreateSlideModal
          shopId={currentShop.id}
          onClose={() => setShowCreateModal(false)}
          onSlideCreated={(newSlide: Slide, autoAnalyze?: boolean) => {
            setShowCreateModal(false);
            setPendingNewSlide({ slide: newSlide, autoAnalyze });
            handleTabChange('slides');
          }}
        />
      )}
    </>
  );
};

export default Dashboard;